import {t} from "i18next";
import {fetchWithMessages} from "./fetchWithMessages";

/**
 * @typedef {object} PersistEntityOptions
 * @property {boolean} [showSuccessNotification] - Show the success message if the save has succeeded
 * @property {boolean} [preserveTimestamps] - If true, disables Mongoose timestamps update in the PATCH request
 */

/**
 * Persists an entity in the backend (create or update)
 *
 * @param {string} endpoint - The endpoint to persist at
 * @param {object} entity - The entity object to persist
 * @param {string} [projectId] - The id of the project the element belongs to
 * @param {function(data: object): Promise<void>} addEntityAction - Callback after creation
 * @param {function(data: object): Promise<void>} updateEntityAction - Callback after update
 * @param {PersistEntityOptions} [options] - Additional options
 * @returns {Promise<string>} The slug (if it has any) or the id of the persisted element
 */
export const persistEntityInBackend = async (
  endpoint,
  entity,
  projectId,
  addEntityAction,
  updateEntityAction,
  {showSuccessNotification = true, preserveTimestamps = false} = {}
) => {
  const baseUrl = projectId ? `projects/${projectId}/${endpoint}` : endpoint;
  if (entity._id === "new") {
    const {_id, ...entityWithoutId} = entity; // Remove the "new" id before sending the request
    const data = await fetchWithMessages(
      baseUrl,
      {method: "POST", body: entityWithoutId},
      {
        statusMessages: {200: showSuccessNotification ? t("common:creationSuccessful") : undefined},
        showLoading: showSuccessNotification,
      }
    );
    await addEntityAction(data);
    return data?.slug || data?._id;
  } else {
    let url = `${baseUrl}/${entity._id}`;
    if (preserveTimestamps) url += "?preserveTimestamps=true";

    const data = await fetchWithMessages(
      url,
      {method: "PATCH", body: entity},
      {
        statusMessages: {
          200: showSuccessNotification ? t("common:modificationsSaved") : undefined,
          401: {type: "error", message: true},
          405: {type: "warning", message: true},
        },
        showLoading: showSuccessNotification,
      }
    );
    await updateEntityAction(data);
    return data?.slug || data?._id;
  }
};
